/**
 * AgentObservabilityScreen — Observabilidade de Agentes.
 *
 * O que cada agente fez, quanto custou (US$), quanto demorou, onde errou e quanto
 * retrabalho gerou — por projeto, por execução e por loop. Somente leitura, sobre
 * GET /observability/* (`agent_catalog:view`; custos só com `budget_costs:view`).
 *
 * Abas: Visão geral · Agente · Execução · Invocações · Qualidade dos dados.
 * O estado (aba, agente, execução, período, projeto) fica na URL — dá para copiar o
 * link de um agente ou de uma execução.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { C } from '@/theme/palette';
import { useProjects } from '@/api/projects';
import { PRESETS, periodoDoFiltro } from './observability/formato';
import { estilos } from './observability/partes';
import { VisaoGeral } from './observability/VisaoGeral';
import { DetalheDoAgente } from './observability/DetalheDoAgente';
import { DetalheDaExecucao } from './observability/DetalheDaExecucao';
import { Invocacoes } from './observability/Invocacoes';
import { QualidadeDosDados } from './observability/QualidadeDosDados';

export const ABAS = [
  { id: 'geral', label: 'Visão geral' },
  { id: 'agente', label: 'Agente' },
  { id: 'execucao', label: 'Execução' },
  { id: 'invocacoes', label: 'Invocações' },
  { id: 'qualidade', label: 'Qualidade dos dados' },
];

export function AgentObservabilityScreen() {
  const [params, setParams] = useSearchParams();
  const s = estilos();

  const aba = ABAS.some((a) => a.id === params.get('aba')) ? params.get('aba') : 'geral';
  const agente = params.get('agente') || '';
  const execucao = params.get('execucao') || '';
  const preset = PRESETS.some((p) => p.id === params.get('periodo')) ? params.get('periodo') : '30';
  const de = params.get('de') || '';
  const ate = params.get('ate') || '';
  const projectId = params.get('projeto') || '';

  /** Atualiza a URL: valor vazio remove o parâmetro. */
  const ir = useCallback((mudancas) => {
    setParams((atual) => {
      const novo = new URLSearchParams(atual);
      Object.entries(mudancas).forEach(([k, v]) => {
        if (v === undefined || v === null || v === '') novo.delete(k);
        else novo.set(k, v);
      });
      return novo;
    });
  }, [setParams]);

  const periodo = useMemo(() => periodoDoFiltro({ preset, de, ate }), [preset, de, ate]);

  const { data: projetosRaw } = useProjects();
  const projetos = useMemo(
    () => (Array.isArray(projetosRaw) ? projetosRaw : projetosRaw?.items ?? []), [projetosRaw]);
  const nomeDoProjeto = useCallback(
    (id) => projetos.find((p) => String(p.id) === String(id))?.name || id, [projetos]);

  const abrirAgente = (nome) => ir({ aba: 'agente', agente: nome });
  const abrirExecucao = (id) => ir({ aba: 'execucao', execucao: id });
  const usaProjeto = aba === 'geral' || aba === 'agente' || aba === 'invocacoes';

  // Largura TOTAL da área de conteúdo (pedido do dono, 2026-10-08): as tabelas de agentes,
  // execuções e invocações têm muitas colunas — o teto de 1320px deixava faixas vazias nos
  // lados em monitor largo. Ficam só as margens laterais.
  return (
    <div style={{ padding: '24px clamp(16px, 4vw, 40px)', width: '100%', boxSizing: 'border-box' }} data-testid="obs-tela">
      <div role="tablist" aria-label="Seções da Observabilidade de Agentes"
        style={{ display: 'flex', flexWrap: 'wrap', gap: 4, borderBottom: `1px solid ${C.blackBorder}`, marginBottom: 16 }}>
        {ABAS.map((a) => {
          const ativa = a.id === aba;
          return (
            <button key={a.id} type="button" role="tab" aria-selected={ativa} onClick={() => ir({ aba: a.id })}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer', padding: '10px 14px', fontSize: 14,
                color: ativa ? C.text : C.neutralDim, fontWeight: ativa ? 600 : 400,
                borderBottom: `2px solid ${ativa ? C.cyber : 'transparent'}`, marginBottom: -1,
              }}>
              {a.label}
            </button>
          );
        })}
      </div>

      {aba !== 'execucao' ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', marginBottom: 18 }}>
          <label style={s.rotuloDeCampo}>
            Período
            <select aria-label="Período" value={preset} style={s.campo}
              onChange={(e) => ir({ periodo: e.target.value === '30' ? '' : e.target.value })}>
              {PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </label>
          {preset === 'personalizado' ? (
            <>
              <label style={s.rotuloDeCampo}>
                De
                <input type="date" aria-label="Início do período" value={de} max={ate || undefined}
                  onChange={(e) => ir({ de: e.target.value })} style={s.campo} />
              </label>
              <label style={s.rotuloDeCampo}>
                Até
                <input type="date" aria-label="Fim do período" value={ate} min={de || undefined}
                  onChange={(e) => ir({ ate: e.target.value })} style={s.campo} />
              </label>
            </>
          ) : null}
          {usaProjeto ? (
            <label style={s.rotuloDeCampo}>
              Projeto
              <select aria-label="Projeto" value={projectId} style={{ ...s.campo, maxWidth: 320 }}
                onChange={(e) => ir({ projeto: e.target.value })}>
                <option value="">Todos os projetos</option>
                {projectId && !projetos.some((p) => String(p.id) === projectId)
                  ? <option value={projectId}>{projectId}</option> : null}
                {projetos.map((p) => <option key={p.id} value={p.id}>{p.name || p.id}</option>)}
              </select>
            </label>
          ) : null}
          <span style={{ color: C.neutralMuted, fontSize: 12, paddingBottom: 8 }}>
            {preset === 'personalizado' && !de && !ate ? 'sem datas: vale o padrão da API (últimos 30 dias) · ' : ''}
            dias contados em UTC
          </span>
        </div>
      ) : null}

      {aba === 'geral' ? (
        <VisaoGeral filtro={{ ...periodo, projectId }} aoAbrirAgente={abrirAgente} />
      ) : aba === 'agente' ? (
        <DetalheDoAgente nome={agente} filtro={{ ...periodo, projectId }} nomeDoProjeto={nomeDoProjeto}
          aoTrocarAgente={(nome) => ir({ agente: nome })} aoAbrirExecucao={abrirExecucao}
          aoVerInvocacoes={(nome) => ir({ aba: 'invocacoes', agente: nome })} />
      ) : aba === 'execucao' ? (
        <DetalheDaExecucao id={execucao} aoBuscar={(id) => ir({ execucao: id })}
          aoAbrirAgente={abrirAgente} nomeDoProjeto={nomeDoProjeto} />
      ) : aba === 'invocacoes' ? (
        <Invocacoes periodo={periodo} projectId={projectId} agenteInicial={agente}
          nomeDoProjeto={nomeDoProjeto} aoAbrirExecucao={abrirExecucao} aoAbrirAgente={abrirAgente} />
      ) : (
        <QualidadeDosDados periodo={periodo} aoAbrirExecucao={abrirExecucao} />
      )}
    </div>
  );
}

export default AgentObservabilityScreen;
